import { useState, useEffect, useCallback } from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import { it } from 'date-fns/locale';
import { callLogsApi, formatApiError } from '../lib/api';
import Layout from '../components/layout/Layout';

// Parse date string ensuring UTC interpretation
// MongoDB stores UTC but may return without timezone marker
const parseUTC = (dateStr) => {
  if (!dateStr) return null;
  const s = String(dateStr);
  // If no timezone info, assume UTC
  if (!s.endsWith('Z') && !s.includes('+') && !/\-\d{2}:\d{2}$/.test(s)) {
    return new Date(s + 'Z');
  }
  return new Date(s);
};
import PageHeader from '../components/layout/PageHeader';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  PhoneCall, 
  PhoneIncoming, 
  PhoneMissed, 
  CheckCircle, 
  XCircle,
  Loader2,
  Clock,
  User,
  MessageSquare,
  CalendarCheck,
  Trash2,
  Archive,
  ArchiveRestore,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const statusConfig = {
  completed: { icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-100', label: 'Completata' },
  in_progress: { icon: PhoneCall, color: 'text-sky-600', bg: 'bg-sky-100', label: 'In corso' },
  failed: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-100', label: 'Fallita' },
  missed: { icon: PhoneMissed, color: 'text-amber-600', bg: 'bg-amber-100', label: 'Persa' }
};

export default function CallLogs() {
  const [callLogs, setCallLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewTab, setViewTab] = useState('active');
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const loadCallLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = { limit: 200, archived: viewTab === 'archived' };
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      const response = await callLogsApi.list(params);
      setCallLogs(response.data);
      setSelectedIds(new Set());
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, viewTab]);

  useEffect(() => {
    loadCallLogs();
  }, [loadCallLogs]);

  const openViewDialog = async (log) => {
    try {
      const response = await callLogsApi.get(log.id);
      setSelectedLog(response.data);
      setViewDialogOpen(true);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0s';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === callLogs.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(callLogs.map(l => l.id)));
    }
  };

  const handleBulkAction = (action) => {
    const count = selectedIds.size;
    const labels = { delete: 'eliminare', archive: 'archiviare', unarchive: 'ripristinare' };
    setConfirmDialog({ action, count, label: labels[action] });
  };

  const executeBulkAction = async () => {
    if (!confirmDialog) return;
    const { action } = confirmDialog;
    const ids = Array.from(selectedIds);
    try {
      setActionLoading(true);
      await callLogsApi.bulkAction(action, ids);
      const labels = { delete: 'eliminati', archive: 'archiviati', unarchive: 'ripristinati' };
      toast.success(`${ids.length} log ${labels[action]}`);
      setConfirmDialog(null);
      loadCallLogs();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setActionLoading(false);
    }
  };

  const handleSingleDelete = async (logId, e) => {
    e.stopPropagation();
    setConfirmDialog({ action: 'delete-single', logId, count: 1, label: 'eliminare' });
  };

  const executeSingleDelete = async () => {
    if (!confirmDialog?.logId) return;
    try {
      setActionLoading(true);
      await callLogsApi.delete(confirmDialog.logId);
      toast.success('Log eliminato');
      setConfirmDialog(null);
      loadCallLogs();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setActionLoading(false);
    }
  };

  const hasSelection = selectedIds.size > 0;
  const allSelected = callLogs.length > 0 && selectedIds.size === callLogs.length;

  return (
    <Layout>
      <PageHeader 
        title="Log Chiamate" 
        subtitle="Cronologia delle chiamate vocali automatiche"
      />

      <div className="content-area" data-testid="call-logs-page">
        {/* Tabs: Active / Archived */}
        <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-lg w-fit mb-5">
          <button
            onClick={() => setViewTab('active')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
              viewTab === 'active'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
            data-testid="tab-active"
          >
            <PhoneCall className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
            Attive
          </button>
          <button
            onClick={() => setViewTab('archived')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
              viewTab === 'archived'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
            data-testid="tab-archived"
          >
            <Archive className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
            Archiviate
          </button>
        </div>

        {/* Filters + Bulk Actions Bar */}
        <div className="flex items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48" data-testid="status-filter">
                <SelectValue placeholder="Tutti gli stati" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                <SelectItem value="completed">Completate</SelectItem>
                <SelectItem value="in_progress">In corso</SelectItem>
                <SelectItem value="failed">Fallite</SelectItem>
                <SelectItem value="missed">Perse</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={loadCallLogs} data-testid="refresh-btn">
              Aggiorna
            </Button>
          </div>

          {/* Bulk action buttons */}
          {hasSelection && (
            <div className="flex items-center gap-2 animate-in fade-in slide-in-from-right-2 duration-200" data-testid="bulk-actions-bar">
              <span className="text-sm text-slate-500 font-medium">
                {selectedIds.size} selezionat{selectedIds.size === 1 ? 'o' : 'i'}
              </span>
              {viewTab === 'active' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkAction('archive')}
                  data-testid="bulk-archive-btn"
                  className="text-amber-700 border-amber-300 hover:bg-amber-50"
                >
                  <Archive className="w-3.5 h-3.5 mr-1.5" />
                  Archivia
                </Button>
              )}
              {viewTab === 'archived' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkAction('unarchive')}
                  data-testid="bulk-unarchive-btn"
                  className="text-sky-700 border-sky-300 hover:bg-sky-50"
                >
                  <ArchiveRestore className="w-3.5 h-3.5 mr-1.5" />
                  Ripristina
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleBulkAction('delete')}
                data-testid="bulk-delete-btn"
                className="text-red-700 border-red-300 hover:bg-red-50"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                Elimina
              </Button>
            </div>
          )}
        </div>

        {/* Call Logs Table */}
        <div className="table-container">
          <Table>
            <TableHeader className="table-header">
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={toggleSelectAll}
                    data-testid="select-all-checkbox"
                    aria-label="Seleziona tutto"
                  />
                </TableHead>
                <TableHead>Chiamata</TableHead>
                <TableHead>Paziente</TableHead>
                <TableHead>Durata</TableHead>
                <TableHead>Prenotazione</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead>Data/Ora</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                  </TableCell>
                </TableRow>
              ) : callLogs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                    {viewTab === 'archived' ? (
                      <>
                        <Archive className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                        Nessuna chiamata archiviata
                      </>
                    ) : (
                      <>
                        <PhoneCall className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                        Nessuna chiamata registrata
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                callLogs.map((log) => {
                  const config = statusConfig[log.status] || statusConfig.failed;
                  const StatusIcon = config.icon;
                  const isSelected = selectedIds.has(log.id);
                  
                  return (
                    <TableRow 
                      key={log.id} 
                      className={`cursor-pointer transition-colors ${isSelected ? 'bg-sky-50 hover:bg-sky-100' : 'hover:bg-slate-50'}`}
                      onClick={() => openViewDialog(log)}
                      data-testid={`call-log-${log.id}`}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => {
                            setSelectedIds(prev => {
                              const next = new Set(prev);
                              if (next.has(log.id)) next.delete(log.id);
                              else next.add(log.id);
                              return next;
                            });
                          }}
                          data-testid={`select-log-${log.id}`}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${config.bg}`}>
                            <PhoneIncoming className={`w-4 h-4 ${config.color}`} />
                          </div>
                          <span className="font-mono text-sm text-slate-600">{log.caller_phone}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {log.patient_name ? (
                          <div>
                            <p className="font-medium text-slate-800">{log.patient_name}</p>
                            <p className="text-xs text-slate-500">Riconosciuto</p>
                          </div>
                        ) : (
                          <span className="text-slate-500 text-sm">Non identificato</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-slate-600">
                          <Clock className="w-3 h-3" />
                          <span className="text-sm">{formatDuration(log.duration_seconds)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {log.appointment_created ? (
                          <span className="badge badge-confirmed">
                            <CalendarCheck className="w-3 h-3 mr-1" />
                            Creata
                          </span>
                        ) : (
                          <span className="text-slate-400 text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className={`badge ${config.bg} ${config.color}`}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {config.label}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {log.started_at && (
                          <>
                            <p>{format(parseUTC(log.started_at), 'dd/MM/yyyy HH:mm', { locale: it })}</p>
                            <p className="text-xs">
                              {formatDistanceToNow(parseUTC(log.started_at), { addSuffix: true, locale: it })}
                            </p>
                          </>
                        )}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={(e) => handleSingleDelete(log.id, e)}
                          className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                          data-testid={`delete-log-${log.id}`}
                          title="Elimina"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {/* Results count */}
        {!loading && callLogs.length > 0 && (
          <p className="text-xs text-slate-400 mt-3">
            {callLogs.length} chiamat{callLogs.length === 1 ? 'a' : 'e'} {viewTab === 'archived' ? 'archiviate' : ''}
          </p>
        )}
      </div>

      {/* Call Log Detail Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="call-log-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>
              Dettaglio Chiamata
            </DialogTitle>
          </DialogHeader>
          {selectedLog && (
            <div className="mt-4 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Numero Chiamante</p>
                  <p className="font-mono text-lg text-slate-800 mt-1">{selectedLog.caller_phone}</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Durata</p>
                  <p className="text-lg text-slate-800 mt-1">{formatDuration(selectedLog.duration_seconds)}</p>
                </div>
              </div>

              {selectedLog.patient_name && (
                <div className="flex items-center gap-3 p-4 border border-slate-200 rounded-lg">
                  <div className="w-10 h-10 bg-violet-100 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-violet-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Paziente riconosciuto</p>
                    <p className="font-medium text-slate-800">{selectedLog.patient_name}</p>
                  </div>
                </div>
              )}

              {selectedLog.appointment_created && (
                <div className="flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <CalendarCheck className="w-5 h-5 text-emerald-600" />
                  <div>
                    <p className="font-medium text-emerald-800">Prenotazione creata</p>
                    <p className="text-sm text-emerald-600">ID: {selectedLog.appointment_id}</p>
                  </div>
                </div>
              )}

              <div>
                <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  Trascrizione Conversazione
                </h4>
                <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-64 overflow-y-auto">
                  {selectedLog.transcript?.length > 0 ? (
                    selectedLog.transcript.map((msg, idx) => (
                      <div 
                        key={idx} 
                        className={`p-3 ${msg.role === 'assistant' ? 'bg-sky-50' : 'bg-white'}`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs font-medium uppercase tracking-wide ${
                            msg.role === 'assistant' ? 'text-sky-600' : 'text-slate-500'
                          }`}>
                            {msg.role === 'assistant' ? 'Assistente' : 'Paziente'}
                          </span>
                          {msg.timestamp && (
                            <span className="text-xs text-slate-400">
                              {format(parseUTC(msg.timestamp), 'HH:mm:ss')}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-700">{msg.content}</p>
                      </div>
                    ))
                  ) : (
                    <div className="p-4 text-center text-slate-500 text-sm">
                      Nessuna trascrizione disponibile
                    </div>
                  )}
                </div>
              </div>

              <div className="text-xs text-slate-500 border-t border-slate-200 pt-4">
                <p>Inizio: {selectedLog.started_at && format(parseUTC(selectedLog.started_at), 'dd/MM/yyyy HH:mm:ss', { locale: it })}</p>
                {selectedLog.ended_at && (
                  <p>Fine: {format(parseUTC(selectedLog.ended_at), 'dd/MM/yyyy HH:mm:ss', { locale: it })}</p>
                )}
                {selectedLog.tts_provider_used && (
                  <p className="mt-1">
                    Voce: <span className={`font-medium ${selectedLog.tts_provider_used.startsWith('google') ? 'text-sky-600' : selectedLog.tts_provider_used.startsWith('realtime') ? 'text-violet-600' : 'text-amber-600'}`}>
                      {selectedLog.tts_provider_used}
                    </span>
                  </p>
                )}
                {selectedLog.realtime_debug && (
                  <p className="mt-1 text-red-500 text-[10px] font-mono">Debug: {selectedLog.realtime_debug}</p>
                )}
                {selectedLog.realtime_attempted !== undefined && (
                  <p className="mt-0.5 text-[10px]">
                    Realtime: {selectedLog.realtime_attempted ? (
                      selectedLog.realtime_connected ? (
                        <span className="text-green-600">ATTIVO (model: {selectedLog.realtime_model_used || 'n/a'}, voice: {selectedLog.realtime_voice_used || 'n/a'})</span>
                      ) : (
                        <span className="text-amber-600">tentato ma fallito (key: {selectedLog.realtime_key_source || 'n/a'}, clinic: {selectedLog.realtime_clinic_id || 'n/a'})</span>
                      )
                    ) : (
                      <span className="text-slate-400">non attivo</span>
                    )}
                  </p>
                )}
                {selectedLog.realtime_error && (
                  <p className="mt-0.5 text-red-500 text-[10px] font-mono" data-testid="realtime-error">
                    Errore Realtime: {selectedLog.realtime_error}
                  </p>
                )}
                {selectedLog.realtime_model_attempted && !selectedLog.realtime_connected && (
                  <p className="mt-0.5 text-slate-500 text-[10px]">
                    Modello tentato: {selectedLog.realtime_model_attempted}
                  </p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm Dialog */}
      <Dialog open={!!confirmDialog} onOpenChange={() => setConfirmDialog(null)}>
        <DialogContent className="sm:max-w-md" data-testid="confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Conferma azione
            </DialogTitle>
          </DialogHeader>
          {confirmDialog && (
            <div className="mt-2">
              <p className="text-sm text-slate-600 mb-6">
                Sei sicuro di voler <strong>{confirmDialog.label}</strong>{' '}
                {confirmDialog.count} log chiamat{confirmDialog.count === 1 ? 'a' : 'e'}?
                {confirmDialog.action === 'delete' || confirmDialog.action === 'delete-single' ? (
                  <span className="block mt-2 text-red-600 text-xs">Questa azione non può essere annullata.</span>
                ) : null}
              </p>
              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => setConfirmDialog(null)}
                  disabled={actionLoading}
                  data-testid="confirm-cancel-btn"
                >
                  Annulla
                </Button>
                <Button
                  variant={confirmDialog.action.includes('delete') ? 'destructive' : 'default'}
                  onClick={confirmDialog.action === 'delete-single' ? executeSingleDelete : executeBulkAction}
                  disabled={actionLoading}
                  data-testid="confirm-action-btn"
                >
                  {actionLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  {confirmDialog.action === 'archive' ? 'Archivia' :
                   confirmDialog.action === 'unarchive' ? 'Ripristina' : 'Elimina'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
